import { AlertTriangle, Ban, Bot, ChevronRight, CircleCheck, CircleHelp, Loader, OctagonX } from "lucide-react"
import type { AgentActivity, AgentActivityGroup, AgentActivityStatus, GroupRenderer } from "../../events/messageGroups"
import { compactInline, stringifyRaw } from "../../events/presentation"

const STATUS_LABELS: Record<AgentActivityStatus, string> = {
    running: "running",
    completed: "completed",
    failed: "failed",
    killed: "killed",
    stopped: "stopped",
    incomplete: "incomplete",
    unknown: "unknown",
}

function statusIcon(status: AgentActivityStatus, size = "0.9em") {
    switch (status) {
        case "running":
            return <Loader size={size} className="animate-spin text-warning flex-shrink-0" />
        case "completed":
            return <CircleCheck size={size} className="text-success flex-shrink-0" />
        case "failed":
            return <OctagonX size={size} className="text-error flex-shrink-0" />
        case "killed":
        case "stopped":
            return <Ban size={size} className="text-warning flex-shrink-0" />
        case "incomplete":
            return <AlertTriangle size={size} className="text-warning flex-shrink-0" />
        case "unknown":
            return <CircleHelp size={size} className="text-muted flex-shrink-0" />
    }
}

function agentFacts(agent: AgentActivity): string[] {
    return [
        agent.taskType,
        agent.model,
        agent.usage?.durationMs ? formatDuration(agent.usage.durationMs) : undefined,
        agent.usage?.totalTokens ? `${agent.usage.totalTokens.toLocaleString()} tokens` : undefined,
        agent.usage?.toolUses ? `${agent.usage.toolUses.toLocaleString()} tool uses` : undefined,
    ].filter((value): value is string => Boolean(value))
}

function formatDuration(durationMs: number): string {
    const seconds = Math.round(durationMs / 1000)
    if (seconds < 60) return `${seconds}s`
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
}

function truncatedRaw(value: unknown): string {
    const raw = stringifyRaw(value)
    return raw.length <= 8000 ? raw : `${raw.slice(0, 8000).trim()}\n... truncated ${raw.length - 8000} characters`
}

function AgentContent({ agent }: { agent: AgentActivity }) {
    const facts = agentFacts(agent)
    return (
        <div className="px-3 py-2">
            <div className="flex min-w-0 items-center gap-2 text-xs">
                {statusIcon(agent.status, "1em")}
                <span className="truncate font-medium text-base-content">{agent.label}</span>
                <span className="text-muted">{STATUS_LABELS[agent.status]}</span>
                {facts.length > 0 && <span className="ml-auto text-right text-muted">{facts.join(" · ")}</span>}
            </div>
            {agent.path && <div className="mt-1 truncate font-mono text-[11px] text-muted">{agent.path}</div>}
            {agent.summary && <div className="mt-1 text-xs text-base-content">{compactInline(agent.summary, 500)}</div>}
            {agent.outputFile && <div className="mt-1 truncate font-mono text-[11px] text-muted">Output: {agent.outputFile}</div>}
            {agent.events.length > 0 && (
                <details className="group mt-2 border-t border-border pt-1">
                    <summary className="btn flex cursor-pointer items-center gap-1 px-0 py-1 text-[11px] text-muted">
                        <ChevronRight size="0.9em" className="transition-transform group-open:rotate-90" />
                        {agent.events.length} lifecycle event{agent.events.length === 1 ? "" : "s"}
                    </summary>
                    <div className="divide-y divide-border border border-border">
                        {agent.events.map((event, index) => (
                            <details key={`${event.messageIndex}-${event.kind}-${index}`}>
                                <summary className="btn flex cursor-pointer items-center gap-2 px-2 py-1.5 text-left text-[11px]">
                                    <ChevronRight size="0.85em" className="flex-shrink-0" />
                                    <span className="font-medium text-base-content">{event.kind.replace(/_/g, " ")}</span>
                                    {event.summary && <span className="truncate text-muted">{compactInline(event.summary, 160)}</span>}
                                    {event.timestamp && <span className="ml-auto font-mono text-muted">{formatTimestamp(event.timestamp)}</span>}
                                </summary>
                                <pre className="max-h-72 overflow-auto whitespace-pre-wrap border-t border-border bg-base-200/50 px-2 py-1.5 text-[11px]">
                                    {truncatedRaw(event.raw)}
                                </pre>
                            </details>
                        ))}
                    </div>
                </details>
            )}
        </div>
    )
}

function formatTimestamp(value: string): string {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? value : date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" })
}

function AgentActivityContent({ group }: { group: AgentActivityGroup }) {
    return (
        <div className="divide-y divide-border bg-base-100">
            {group.agents.map((agent) => (
                <AgentContent key={agent.id} agent={agent} />
            ))}
        </div>
    )
}

export function formatAgentActivityLabel(group: AgentActivityGroup): string {
    const counts = new Map<AgentActivityStatus, number>()
    for (const agent of group.agents) {
        counts.set(agent.status, (counts.get(agent.status) ?? 0) + 1)
    }
    const details = (["running", "completed", "failed", "killed", "stopped", "incomplete", "unknown"] satisfies AgentActivityStatus[])
        .map((status) => {
            const count = counts.get(status)
            return count ? `${count} ${STATUS_LABELS[status]}` : null
        })
        .filter((value): value is string => value !== null)
    const noun = group.agents.length === 1 ? "agent" : "agents"
    return [`${group.agents.length} ${noun}`, ...details].join(" · ")
}

export const agentActivityRenderer: GroupRenderer<AgentActivityGroup> = {
    getLabel: formatAgentActivityLabel,
    getIcon: () => <Bot size="0.9em" className="text-primary flex-shrink-0" />,
    getStatusIcon: (group) => (group.hasGaps ? <AlertTriangle size="1em" className="text-warning flex-shrink-0" /> : null),
    getHeaderInfo: (group) => <span className="text-xs text-muted">{group.harnessId === "claude-code" ? "Claude Code" : "Codex"}</span>,
    renderContent: (group) => <AgentActivityContent group={group} />,
}
